import { FormEvent, useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchAdminProjectAccess } from '@frontend/api-clients/projectAccessClient';
import { postProjectMembershipV2, deleteProjectMembershipV2 } from '@frontend/api-clients/adminMembershipClient';
import { adminCreateProject } from '@frontend/api-clients/adminProjectClient';
import { getEcosystems } from '@frontend/api-clients/ecosystemsClient';
import type { EcosystemDefinition, User, AdminProjectAccessProject } from '@frontend/types';
import { usePublicIntegration } from '../../context/PublicIntegrationContext';
import { buildAdminLinks } from '@frontend/lib/admin-hub-links';

interface AdminDashboardPageProps {
  user: User;
}

export default function AdminDashboardPage({ user }: AdminDashboardPageProps) {
  const appOrigin = typeof window === 'undefined' ? '' : window.location.origin;
  const integration = usePublicIntegration();
  const adminLinks = useMemo(() => buildAdminLinks(appOrigin, integration), [appOrigin, integration]);
  const [selectedEcosystem, setSelectedEcosystem] = useState('');
  const [ecosystemDefs, setEcosystemDefs] = useState<EcosystemDefinition[]>([]);
  const [projects, setProjects] = useState<AdminProjectAccessProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailInput, setEmailInput] = useState<Record<string, string>>({});
  const [roleInput, setRoleInput] = useState<Record<string, string>>({});
  const [busyProject, setBusyProject] = useState<string | null>(null);
  const [createProjectId, setCreateProjectId] = useState('');
  const [createProjectName, setCreateProjectName] = useState('');
  const [createProjectDescription, setCreateProjectDescription] = useState('');
  const [busyCreateProject, setBusyCreateProject] = useState(false);

  const ecosystemOptions = useMemo(
    () => ecosystemDefs.map((e) => ({ id: e.id, label: e.displayName })),
    [ecosystemDefs]
  );

  const loadProjectAccess = async (ecosystemId: string) => {
    setLoading(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) {
        throw new Error('You must be signed in to manage project access.');
      }

      const loadedProjects = await fetchAdminProjectAccess(ecosystemId, token);
      setProjects(loadedProjects);
      setRoleInput((current) => {
        const next = { ...current };
        for (const project of loadedProjects) {
          if (!next[project.id]) {
            next[project.id] = 'viewer';
          }
        }
        return next;
      });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load project access');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadEcosystems = async () => {
      const token = authClient.getToken();
      if (!token) return;
      try {
        const defs = await getEcosystems(token);
        setEcosystemDefs(defs);
        if (defs.length > 0) setSelectedEcosystem(defs[0].id);
      } catch {
        // Non-fatal: keep empty options
      }
    };
    loadEcosystems();
  }, []);

  useEffect(() => {
    if (!selectedEcosystem) return;
    loadProjectAccess(selectedEcosystem);
  }, [selectedEcosystem]);

  const createProject = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setBusyCreateProject(true);
    try {
      const token = authClient.getToken();
      if (!token) {
        throw new Error('You must be signed in to manage project access.');
      }
      await adminCreateProject(token, {
        id: createProjectId.trim(),
        ecosystemId: selectedEcosystem,
        name: createProjectName.trim(),
        description: createProjectDescription.trim() || undefined,
      });
      setCreateProjectId('');
      setCreateProjectName('');
      setCreateProjectDescription('');
      setSuccess('Project created.');
      await loadProjectAccess(selectedEcosystem);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project');
    } finally {
      setBusyCreateProject(false);
    }
  };

  const addMember = async (event: FormEvent, projectId: string) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setBusyProject(projectId);

    try {
      const token = authClient.getToken();
      if (!token) {
        throw new Error('You must be signed in to manage project access.');
      }

      const email = (emailInput[projectId] || '').trim();
      const role = (roleInput[projectId] || 'viewer').trim();

      const data = await postProjectMembershipV2(token, {
          projectId,
          email,
          role,
          ensureEcosystemMembership: true,
          syncDownstream: true,
        });

      setEmailInput((current) => ({ ...current, [projectId]: '' }));
      setSuccess(
        data?.projectionStatus === 'synced' || data?.projectionStatus === 'skipped'
          ? 'Project membership updated and projected.'
          : `Membership saved; projection ${data?.projectionStatus || 'partial'}.`
      );
      await loadProjectAccess(selectedEcosystem);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update project member');
    } finally {
      setBusyProject(null);
    }
  };

  const removeMember = async (projectId: string, userPk: string) => {
    setError('');
    setSuccess('');
    setBusyProject(projectId);

    try {
      const token = authClient.getToken();
      if (!token) {
        throw new Error('You must be signed in to manage project access.');
      }

      await deleteProjectMembershipV2(token, { projectId, userPk, syncDownstream: true });

      setSuccess('Project membership removed from portal and downstream where configured.');
      await loadProjectAccess(selectedEcosystem);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to remove project member');
    } finally {
      setBusyProject(null);
    }
  };

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="navbar-brand">Patriotic Projects Admin</div>
        <div className="navbar-menu">
          <span className="dashboard-welcome">Signed in as {user.name || user.username}</span>
          <a href="/dashboard" className="btn btn-secondary">Back to Dashboard</a>
        </div>
      </nav>

      <div className="container dashboard-container">
        <div className="dashboard-hero">
          <h1 className="dashboard-title">Administration Hub</h1>
          <p className="dashboard-subtitle">Manage all core ecosystem platforms from one place.</p>
        </div>

        <div className="ecosystem-card" style={{ padding: '1.2rem 1.4rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <h2 className="ecosystem-title" style={{ marginBottom: '0.25rem' }}>Ecosystem Registry</h2>
              <p className="ecosystem-description" style={{ margin: 0 }}>
                Create, edit, and decommission ecosystems in the branding registry.
              </p>
            </div>
            <a href="/admin/ecosystems" className="btn btn-primary">Manage Ecosystems →</a>
          </div>
        </div>

        <div className="ecosystem-card admin-access-card">
          <div className="admin-access-header">
            <h2 className="ecosystem-title">Project Access Control</h2>
            <select
              value={selectedEcosystem}
              onChange={(event) => setSelectedEcosystem(event.target.value)}
              className="admin-ecosystem-select"
            >
              {ecosystemOptions.map((ecosystem) => (
                <option key={ecosystem.id} value={ecosystem.id}>
                  {ecosystem.label}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="settings-message settings-error">{error}</div>}
          {success && <div className="settings-message settings-success">{success}</div>}

          {selectedEcosystem ? (
            <form className="admin-member-form" style={{ marginTop: '1rem' }} onSubmit={createProject}>
              <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>Create project</div>
              <p className="ecosystem-description" style={{ marginTop: 0 }}>
                Adds a catalog row for the selected ecosystem (default paths for OpenProject, Nextcloud, Gitea).
              </p>
              <input
                type="text"
                placeholder="project-id (kebab-case)"
                value={createProjectId}
                onChange={(e) => setCreateProjectId(e.target.value)}
                required
                pattern="[a-z][a-z0-9-]*"
                title="Lowercase letters, digits, hyphens"
              />
              <input
                type="text"
                placeholder="Display name"
                value={createProjectName}
                onChange={(e) => setCreateProjectName(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={createProjectDescription}
                onChange={(e) => setCreateProjectDescription(e.target.value)}
              />
              <button type="submit" className="btn btn-secondary" disabled={busyCreateProject || loading}>
                {busyCreateProject ? 'Creating…' : 'Create project'}
              </button>
            </form>
          ) : null}

          {loading ? (
            <p className="ecosystem-description">Loading project access...</p>
          ) : projects.length === 0 ? (
            <p className="ecosystem-description">No projects configured for this ecosystem.</p>
          ) : (
            <div className="admin-project-list">
              {projects.map((project) => (
                <div key={project.id} className="admin-project-card">
                  <h3 className="project-visibility-name">{project.name}</h3>
                  <p className="project-visibility-meta">{project.description}</p>

                  <div className="admin-member-list">
                    {project.members.length === 0 ? (
                      <p className="project-visibility-empty">No explicit members yet.</p>
                    ) : (
                      project.members.map((member) => (
                        <div key={`${project.id}:${member.userPk}`} className="admin-member-item">
                          <div>
                            <div className="admin-member-name">{member.name || member.username || member.email}</div>
                            <div className="project-visibility-meta">{member.email} - {member.role}</div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busyProject === project.id}
                            onClick={() => removeMember(project.id, member.userPk)}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <form className="admin-member-form" onSubmit={(event) => addMember(event, project.id)}>
                    <input
                      type="email"
                      placeholder="user@example.com"
                      value={emailInput[project.id] || ''}
                      onChange={(event) =>
                        setEmailInput((current) => ({ ...current, [project.id]: event.target.value }))
                      }
                      required
                    />
                    <select
                      value={roleInput[project.id] || 'viewer'}
                      onChange={(event) =>
                        setRoleInput((current) => ({ ...current, [project.id]: event.target.value }))
                      }
                    >
                      <option value="viewer">viewer</option>
                      <option value="contributor">contributor</option>
                      <option value="maintainer">maintainer</option>
                      <option value="admin">admin</option>
                    </select>
                    <button type="submit" className="btn btn-primary" disabled={busyProject === project.id}>
                      {busyProject === project.id ? 'Saving...' : 'Add Member'}
                    </button>
                  </form>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-2 admin-grid">
          {adminLinks.map((item) => (
            <a
              key={item.name}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="ecosystem-card admin-destination-card"
            >
              <div className="ecosystem-icon">{item.icon}</div>
              <h2 className="ecosystem-title">{item.name}</h2>
              <p className="ecosystem-description">{item.description}</p>
              <span className="admin-open-label">Open Console</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
